import {
  type CONTENT_TYPES,
  type PerContentListProps,
} from '@hivebrite/design-system/ContentList';

import { AVAILABLE_CONTENT_TYPES } from 'hb-react/shared/components/ContentRecommendation/constants';
import {
  type RecommendedContents,
  type AvailableContentTypes,
} from 'hb-react/shared/components/ContentRecommendation/types';
import getDateStr from 'hb-react/shared/components/EventDate/utils/getDateStr';
import dayjs from 'hb-react/shared/utils/dayjs/dayjs';
import { type TimeZoneConfiguration } from 'hb-react/types/timeZoneConfiguration';

export const formatRecommendedContentData = (
  item: RecommendedContents[AvailableContentTypes],
  timeZoneConfiguration: TimeZoneConfiguration,
): PerContentListProps[CONTENT_TYPES] => {
  const baseProps = {
    displayFields: {
      content: true,
      contentType: true,
      group: true,
    },
  };

  switch (item.type) {
    case AVAILABLE_CONTENT_TYPES.NEWS:
      return {
        ...baseProps,
        contentType: 'news',
        content: item.sanitizedContent,
        id: item.id,
        image: item.coverPicture?.mediumUrl,
        title: item.subject,
      } as PerContentListProps[CONTENT_TYPES.NEWS];

    case AVAILABLE_CONTENT_TYPES.FORUM:
      return {
        ...baseProps,
        contentType: AVAILABLE_CONTENT_TYPES.FORUM,
        author: {
          id: item.user.id,
          name: item.user.name,
          photoThumbUrl: item.user.photo.thumbnailUrl,
        },
        content: item.sanitizedContent,
        displayFields: {
          ...baseProps.displayFields,
          author: true,
        },
        id: item.id,
        imageSrc: item.coverPicture?.mediumUrl,
        name: item.title,
      } as PerContentListProps[CONTENT_TYPES.FORUM];

    case AVAILABLE_CONTENT_TYPES.USER_EVENT:
      return {
        ...baseProps,
        ...formatCommonEventData(item, baseProps, timeZoneConfiguration),
        image: item.logo?.mediumUrl,
      } as PerContentListProps[CONTENT_TYPES.EVENT];

    case AVAILABLE_CONTENT_TYPES.NETWORK_EVENT:
      return {
        ...baseProps,
        ...formatCommonEventData(item, baseProps, timeZoneConfiguration),
        onlineEvent: Boolean(item.webinarUrl || item.isInternalZoomMeeting),
        image: item.coverPicture?.mediumUrl,
      } as PerContentListProps[CONTENT_TYPES.EVENT];

    default:
      throw new Error(`Unsupported content`);
  }
};

const formatCommonEventData = (
  item,
  baseProps,
  timeZoneConfiguration: TimeZoneConfiguration,
) => {
  const { currentUserTimezone, eventDatesInUserTimezone } =
    timeZoneConfiguration || {};
  const targetTimeZone = eventDatesInUserTimezone
    ? currentUserTimezone
    : item.timezone;

  return {
    contentType: 'event',
    date: getDateStr({
      startDate: item.startDate,
      // We don't want to display the endDate in the content recommendation list
      endDate: undefined,
      timezone: targetTimeZone,
    }),
    displayFields: {
      ...baseProps.displayFields,
      location: true,
      date: true,
      group: true,
    },
    id: item.id,
    location: item?.location?.location,
    formattedDate: {
      day: dayjs(item.startDate).format('DD'),
      month: dayjs(item.startDate).format('MM'),
    },
    name: item.title,
    // Attendees are expected in monorepo, but not needed in content reco list
    // displayFields.attendees is false by default
    attendees: [],
    totalAttendees: 0,
  };
};
